import React, { useState } from 'react';
import {
  IonButton,
  IonRow,
  IonCol,
  IonAlert,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonLoading,
  IonIcon
} from '@ionic/react';
import { format } from '@buttercup/react-formatted-input';
import { Patient } from '../models';
import PatientRepository from '../repositories/PatientRepository';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { STRINGS } from '../i18n/strings';
import FormError from './FormError';
import { validate as nirValidate } from 'validcq';
import EventBus from '../services/EventBus';
import { nirFormat, prefixedPhoneFormat } from '../const';
import { calculateAge } from '../utils/date';
import { informationCircleOutline } from 'ionicons/icons';
import Field from './Field';
import './RegistrationForm/style.scss';
import {
  noSpecialCharacters,
  noSpecialCharactersNorNumbersFilter
} from '../utils/validators';
import { connect } from 'react-redux';
import { AppointmentsRootState } from '../store/reducers/appointments';
import isIOS from '../utils/isIOS';
import moment from 'moment';
type Inputs = {
  lastName: string;
  firstName: string;
  birthName: string;
  sameBirthName: boolean;
  gender: 'm' | 'f' | 'nc' | null;
  birthdate: string | null;
  nir: string;
  noNir: boolean;
  nirAlternative: string;
  line1: string;
  postcode: string;
  city: string;
  email: string;
  phone: string;
  mutual: File | null;
  referringDoctor: string;
  consent_use_of_medical_data: boolean;
};

type Props = {
  onConfirmForm: (userData: Patient) => void;
  returnOnly?: boolean;
};

const mapStateToProps = (
  state: {
    appointments: AppointmentsRootState;
  },
  ownProps: any
) => {
  return {
    currentAppointmentLaboratory: state.appointments.appointment.laboratory
  };
};

const NewPatientForm: React.FC<ReturnType<typeof mapStateToProps> & Props> = ({
  onConfirmForm,
  returnOnly = false,
  currentAppointmentLaboratory = {}
}) => {
  const { handleSubmit, control, watch, trigger, errors, setValue, getValues } =
    useForm<Inputs>({
      mode: 'onChange',
      defaultValues: {
        email: '',
        lastName: '',
        firstName: '',
        birthName: '',
        sameBirthName: false,
        gender: null,
        birthdate: null,
        nir: '',
        noNir: false,
        nirAlternative: '',
        phone: '',
        line1: '',
        postcode: '',
        city: '',
        referringDoctor: '',
        consent_use_of_medical_data: false,
        mutual: null
      }
    });

  const watchAllFields = watch();

  const { registration_options } = currentAppointmentLaboratory;
  const hasAbroadNir =
    currentAppointmentLaboratory &&
    registration_options &&
    registration_options.no_nir;
  const hasCertificate =
    currentAppointmentLaboratory &&
    registration_options &&
    registration_options.certificate_reimbursement;
  const isMutualRequired =
    currentAppointmentLaboratory &&
    registration_options &&
    registration_options.mutual_required;
  const [nirAlternative, setNirAlternative] = useState<File>();
  const [ticketModerateur, setTicketModerateur] = useState<File>();

  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationAlert, setShowConfirmationAlert] = useState(false);

  async function onSubmitForm(data: Inputs) {
    let splittedBirthdate: any = [];
    let birthdateFormatted: any = moment(data.birthdate).format('YYYY-MM-DD');
    if (data.birthdate) {
      splittedBirthdate = data.birthdate.split('/');
      birthdateFormatted = `${splittedBirthdate[2]}-${splittedBirthdate[1]}-${splittedBirthdate[0]}`;
    }
    setIsLoading(true);

    let patient = new Patient({
      email: data.email,
      lastName: data.lastName,
      firstName: data.firstName,
      birthName: data.birthName,
      gender: data.gender || 'nc',
      birthdate: birthdateFormatted,
      nir: data.nir.replace(/[^0-9a-zA-Z)]/g, ''),
      phone: data.phone,
      // @ts-ignore. NOTE: This is temporary.
      referringDoctor: data.searchReferringDoctor,
      address: {
        line1: data.line1,
        postcode: data.postcode,
        city: data.city,
        country: 'fr'
      },
      mutual: data.mutual,
      ticketModerateur,
      nirAlternative
    });

    let newPatient = patient;

    if (!returnOnly) {
      try {
        newPatient = await PatientRepository.create(patient);
      } catch (e) {
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(false);
    EventBus.success(
      `Votre proche, ${patient.getFullName()}, a bien été enregistré.`
    );
    onConfirmForm(newPatient);
  }

  function showError(fieldName: keyof Inputs) {
    return (
      (errors as any)[fieldName] && (
        <ErrorMessage
          errors={errors}
          name={fieldName}
          render={({ message }) => <FormError message={message as string} />}
        />
      )
    );
  }
  const birthdate = getValues('birthdate');
  const birthdateArray = birthdate ? birthdate.split('/').filter(Boolean) : [];
  const birthdateFormat = 'DD/MM/YYYY';
  let birthdateObject;
  if (birthdateArray.length === 3 && birthdateArray[2].length === 4) {
    birthdateObject = moment(getValues('birthdate') as string, birthdateFormat);
  } else {
    birthdateObject = null;
  }

  return (
    <form
      className="registration-form"
      onKeyDown={async (e) => {
        if (e.which === 13) {
          const isValid = await trigger();
          if (isValid) {
            setShowConfirmationAlert(true);
          }
        }
      }}
    >
      <IonLoading isOpen={isLoading} />
      <div>
        <IonRow className="registration-form__fieldset">
          <fieldset>
            <legend className="registration-form__title">
              Vos informations de compte
            </legend>
            <IonRow>
              <Field
                type="email"
                controllerProps={{
                  control,
                  name: 'email',
                  rules: {
                    pattern: {
                      value:
                        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
                      message: STRINGS.FR.FORM_ERROR_FIELD_EMAIL_INCORRECT
                    },
                    required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED
                  }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'lepass@unilabs.fr'
                }}
                label="Email"
              />
            </IonRow>
          </fieldset>
        </IonRow>
        <IonRow className="registration-form__fieldset">
          <fieldset>
            <legend className="registration-form__title">
              Vos informations personnelles
            </legend>
            <IonRow>
              <Field
                type="text"
                controllerProps={{
                  control,
                  name: 'firstName',
                  rules: {
                    required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED,
                    validate: noSpecialCharactersNorNumbersFilter
                  }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'Nathalie',
                  autocapitalize: 'on'
                }}
                isMarginless
                label="Prénom"
              />
              <Field
                type="text"
                controllerProps={{
                  control,
                  name: 'lastName',
                  rules: {
                    required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED,
                    validate: noSpecialCharactersNorNumbersFilter
                  }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'Dupont',
                  autocapitalize: 'on'
                }}
                onIonInput={(e, onChange) => {
                  const value = (e.target as HTMLInputElement).value;
                  onChange(value);
                  if (getValues('sameBirthName')) {
                    setValue('birthName', value);
                  }
                }}
                isMarginless
                label="Nom (d'usage)"
              />
            </IonRow>
            <IonRow>
              <IonCol>
                <IonItem color="transparent" lines="none">
                  <IonLabel>Mon nom de naissance est identique</IonLabel>
                  <Controller
                    control={control}
                    name="sameBirthName"
                    render={({ onChange, ref, value, name }) => (
                      <IonCheckbox
                        name={name}
                        checked={value}
                        onIonChange={(e) => {
                          setValue('birthName', getValues('lastName'));
                          onChange(e.detail.checked);
                        }}
                        ref={ref}
                      />
                    )}
                  />
                </IonItem>
                <Field
                  type="text"
                  controllerProps={{
                    control,
                    name: 'birthName',
                    rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                  }}
                  errors={errors}
                  inputProps={{
                    placeholder: 'Martin',
                    autocomplete: 'new-password',
                    autocapitalize: 'on'
                  }}
                  onIonInput={(e, onChange) => {
                    const value = (e.target as HTMLInputElement).value;
                    onChange(value);
                    if (getValues('sameBirthName')) {
                      setValue('lastName', value);
                    }
                  }}
                  label={<>Nom de naissance</>}
                  note={`Le nom de naissance doit être identiqué si différent de votre nom d'usage`}
                />
              </IonCol>
            </IonRow>
            <IonRow>
              <Field
                type="options"
                controllerProps={{
                  control,
                  name: 'gender',
                  rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                }}
                optionsProps={{
                  items: [
                    { value: 'm', name: 'Homme' },
                    { value: 'f', name: 'Femme' }
                  ]
                }}
                errors={errors}
                label="Sexe"
              />
            </IonRow>
            <IonRow>
              <Field
                type="datetime-local"
                controllerProps={{
                  control,
                  name: 'birthdate',
                  rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'Date de naissance'
                }}
                label="Date de naissance"
              />
            </IonRow>
            <IonRow>
              <Field
                type="text"
                controllerProps={{
                  control,
                  name: 'line1',
                  rules: {
                    required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED,
                    validate: noSpecialCharacters
                  }
                }}
                errors={errors}
                inputProps={{
                  placeholder: '1 rue Mozart',
                  autocapitalize: 'on'
                }}
                label="Rue et numéro de rue"
              />
            </IonRow>
            <IonRow>
              <Field
                type={isIOS() ? 'tel' : 'number'}
                controllerProps={{
                  control,
                  name: 'postcode',
                  rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                }}
                errors={errors}
                inputProps={{
                  placeholder: '92110'
                }}
                label="Code postal"
              />
              <Field
                type="text"
                controllerProps={{
                  control,
                  name: 'city',
                  rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'Clichy',
                  autocapitalize: 'on'
                }}
                label="Ville"
              />
            </IonRow>
            <IonRow>
              <Field
                type="tel"
                hasSpecialPhoneInput
                controllerProps={{
                  control,
                  name: 'phone',
                  rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'Numéro de téléphone'
                }}
                label="Téléphone"
              />
            </IonRow>
          </fieldset>
        </IonRow>
        <IonRow className="registration-form__fieldset">
          <fieldset>
            <legend className="registration-form__title">
              Vos informations de santé
            </legend>

            <IonRow>
              <Field
                type="text"
                isFormatted
                controllerProps={{
                  control,
                  name: 'nir',
                  rules: {
                    validate: (value) =>
                      nirValidate(`${value}`) ||
                      !!getValues('noNir') ||
                      STRINGS.FR.FORM_ERROR_FIELD_NIR_INCORRECT,
                    required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED
                  }
                }}
                errors={errors}
                inputProps={{
                  format: nirFormat,
                  placeholder: '1 96 11 75 005 005 32'
                }}
                label="N° Sécurité sociale"
              >
                {hasAbroadNir && (
                  <IonItem color="transparent" lines="none">
                    <IonLabel>
                      Je ne suis pas affilié à la sécurité sociale française
                    </IonLabel>
                    <Controller
                      control={control}
                      name="noNir"
                      render={({ onChange, ref, value, name }) => (
                        <IonCheckbox
                          name={name}
                          checked={value}
                          onIonChange={(e) => {
                            onChange(e.detail.checked);
                          }}
                          ref={ref}
                        />
                      )}
                    />
                  </IonItem>
                )}
                {birthdateObject?.isValid() &&
                calculateAge(birthdateObject.toDate()) < 16 ? (
                  <IonItem lines="none" className="transparent">
                    <IonIcon icon={informationCircleOutline} slot="start" />
                    <span>
                      Veuillez renseigner le numéro de sécurité sociale d'un
                      parent
                    </span>
                  </IonItem>
                ) : null}
                {hasAbroadNir && watch('noNir') ? (
                  <>
                    <IonLabel className="registration-form__label">
                      <small>
                        Si vous dépendez de la CFE, du 100% GMC HENNER ou
                        disposez d'une prise en charge ambassade, merci
                        d'ajouter votre justificatif
                      </small>
                    </IonLabel>
                    <input
                      name="nirAlternative"
                      type="file"
                      className="custom-input"
                      accept=".pdf, image/png, image/jpeg"
                      onChange={(e) => {
                        setNirAlternative(
                          e.target.files ? e.target.files[0] : undefined
                        );
                      }}
                    />
                  </>
                ) : null}
              </Field>
            </IonRow>

            <IonRow>
              <Field
                type="file"
                controllerProps={{
                  control,
                  name: 'mutual',
                  rules: isMutualRequired
                    ? {
                        required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED
                      }
                    : {}
                }}
                inputProps={{
                  accept: '.pdf, image/png, image/jpeg',
                  className: 'custom-input',
                  name: 'mutual'
                }}
                errors={errors}
                hasOptionalWording
                label="Mutuelle"
              />
            </IonRow>
          </fieldset>
        </IonRow>

        {hasCertificate && (
          <IonRow>
            <Field
              type="file"
              inputProps={{
                accept: '.pdf, image/png, image/jpeg',
                className: 'custom-input',
                name: 'ticketModerateur',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  setTicketModerateur(
                    e.target.files ? e.target.files[0] : undefined
                  );
                }
              }}
              label={
                <>
                  Attestation de prise en charge à 100%{' '}
                  <small>(facultatif)</small>
                </>
              }
            />
          </IonRow>
        )}
        <IonRow>
          <Field
            type="text"
            controllerProps={{
              control,
              name: 'searchReferringDoctor',
              rules: {
                validate: noSpecialCharactersNorNumbersFilter
              }
            }}
            errors={errors}
            inputProps={{
              placeholder: 'Nom du médecin traitant (facultatif)',
              autocomplete: 'new-password'
            }}
          />
        </IonRow>
        <IonItem lines="none" className="no-background">
          <Controller
            control={control}
            name="consent_use_of_medical_data"
            rules={{
              validate: (value) =>
                value === true || STRINGS.FR.FORM_ERROR_FIELD_REQUIRED
            }}
            render={({ onChange, onBlur, ref, value, name }) => (
              <IonCheckbox
                name={name}
                checked={value}
                onIonChange={(e) => onChange(e.detail.checked)}
                onIonBlur={onBlur}
                className="ion-margin-end"
                ref={ref}
              />
            )}
          />
          <IonLabel className="ion-text-wrap">
            J'accepte que mes données soient envoyées et utilisées uniquement au
            laboratoire à des fins médicales.
          </IonLabel>
        </IonItem>
        {showError('consent_use_of_medical_data')}
      </div>
      <IonButton
        expand="block"
        onClick={async () => {
          const isValid = await trigger();
          if (isValid) {
            setShowConfirmationAlert(true);
          }
        }}
      >
        Ajouter ce proche
      </IonButton>
      <IonAlert
        isOpen={showConfirmationAlert}
        onDidDismiss={() => setShowConfirmationAlert(false)}
        header={"Confirmez-vous l'ajout ?"}
        subHeader={
          'Assurez-vous que les données renseignées sont bien exactes.'
        }
        message={`${watchAllFields.firstName} ${
          watchAllFields.lastName
        }<br/>né${
          watchAllFields.gender === 'f' ? 'e' : ''
        } le ${birthdateObject?.format(
          birthdateFormat
        )}<br/><br/>N° de Sécurité Sociale<br/>${
          format(watchAllFields.nir, nirFormat).formatted
        }<br/><br/>${
          watchAllFields.phone &&
          format(watchAllFields.phone, prefixedPhoneFormat).formatted
        }<br/>${watchAllFields.email}<br/><br/>${watchAllFields.line1},<br/>${
          watchAllFields.postcode
        } ${watchAllFields.city}`}
        buttons={[
          {
            text: 'Annuler',
            role: 'cancel',
            handler: () => setShowConfirmationAlert(false)
          },
          {
            text: 'Confirmer',
            handler: handleSubmit(onSubmitForm)
          }
        ]}
      />
    </form>
  );
};

export default connect(mapStateToProps, null)(NewPatientForm);
